import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, FolderPlus, Package, X, Archive, Check, CircleDollarSign, ShieldQuestion, TrendingUp, PackageX } from 'lucide-react';
import RecipeCategoryTree from '../receptura/RecipeCategoryTree';
import { ListSkeleton, HlavickaUzka } from '../receptura/RecipeListPanel';
import { StavBunka } from '../receptura/PrehledBunky';
import { MARZE_BARVY } from '../receptura/prehledGrid';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry, pocetAktivnichFiltru } from '../layout/ListFiltr';
import { SECTION_COLOR } from '../receptura/receptUtils';
import { useVyberPolozek, pocetPolozek } from '../receptura/useVyberPolozek';
import MaterialListRow from './MaterialListRow';
import { useViditelneSloupceMaterialu, zmenaCeny, smerZmenyCeny, stavAlergenu, GRID_MAT } from './materialGrid';
import { HlavickaMrizkyMaterialu, AlergenyBunka, DodavatelBunka, CenaBunka, PouzitoBunka } from './MaterialPrehledBunky';

const BEZ_KAT = '__bez__';

// Hodnoty skupin filtru: volby = pole (NEBO uvnitř skupiny), dropdown = jedno id.
const PRAZDNE_FILTRY = { kategorie: null, dodavatel: null, alergeny: [], alergen: [], cena: [], vyvoj: [], pouziti: [] };

const kategorieVolby = (nodes, uroven = 0, acc = []) => {
    for (const n of nodes || []) {
        acc.push({ value: n.id, label: n.nazev, uroven });
        if (n.children?.length) kategorieVolby(n.children, uroven + 1, acc);
    }
    return acc;
};

const collectCatIds = (nodes, acc = []) => {
    for (const n of nodes || []) {
        acc.push(n.id);
        if (n.children?.length) collectCatIds(n.children, acc);
    }
    return acc;
};

/**
 * Seznam surovin — první sloupec standardního rozvržení modulu (norma UI,
 * viz standard-rozvrzeni.md; vzor RecipeListPanel u polotovarů a produktů).
 *
 * `siroky` = přehled přes celou šířku (nic není vybráno): řádky se rozloží do
 * sloupcové mřížky a kategorie nesou souhrn za podstrom. V úzkém režimu (vedle
 * detailu) je to dvouřádková karta a ikonová hlavička.
 *
 * Co se u suroviny hlídá: platná cena (bez ní vstupuje do kalkulace nulou) a
 * vyplněné alergeny. Z toho je semafor řádku i pohledy „Bez ceny" a „Alergeny
 * nevyplněny". Pohled „Zdražilo" porovnává s předchozí cenou téhož dodavatele.
 */
export default function SurovinyListPanel({
    isDark, mutedColor, secondaryColor, cardStyle, inputStyle, siroky = false, kompaktni = false,
    search, setSearch, katStrom, items, loading,
    activeId, onSelect, onNew,
    onCatSave, onCatAdd, onCatDelete, onMoveItems, onReparentCat, onKategorieKlik,
    dodavatele = [], alergenyList = [],
    // Proklik z karty dodavatele ve Financích → předvyplněný filtr dodavatele.
    dodavatelZvenku = null,
    // Místo pod posledním řádkem (mobil: Palec obalu). Patří dovnitř scrollu,
    // ne pod kartu — seznam má jít až ke spodní hraně displeje.
    rezervaDole = null,
}) {
    const searchActive = !!search.trim();
    const sloupce = useViditelneSloupceMaterialu(siroky);
    const vyber = useVyberPolozek('MAT');

    const [expanded, setExpanded] = useState(() => new Set());
    const [pohled, setPohled] = useState('aktivni');
    const [filtry, setFiltry] = useState(PRAZDNE_FILTRY);

    useEffect(() => {
        if (dodavatelZvenku == null) return;
        setPohled('aktivni');
        setFiltry(f => ({ ...f, dodavatel: dodavatelZvenku }));
    }, [dodavatelZvenku]);

    // „Nová kategorie" je v hlavičce (norma), modal vlastní strom.
    const otevritNovouKat = useRef(null);
    const registrujNovouKat = useCallback((fn) => { otevritNovouKat.current = fn; }, []);

    // Semafor položky — obdoba validace receptu, jen s pravidly, která dávají
    // smysl u materiálu. Strom si z `validace` sám poskládá počty u kategorií.
    const polozky = useMemo(() => (items || []).map(it => {
        const chyby = [];
        const varovani = [];
        if (it.aktualni_cena == null) chyby.push('Chybí platná cena');
        if (stavAlergenu(it) === 'nevyplneno') varovani.push('Alergeny nevyplněny');
        const stav = chyby.length ? 'red' : (varovani.length ? 'orange' : null);
        return { ...it, validace: { stav, chyby, varovani, tipy: [] } };
    }), [items]);

    const hledane = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return polozky;
        return polozky.filter(it => it.nazev?.toLowerCase().includes(q) || it.kod?.toLowerCase().includes(q));
    }, [polozky, search]);

    const { aktivni, archiv } = useMemo(() => {
        const akt = [], arch = [];
        for (const it of hledane) (it.aktivni === false ? arch : akt).push(it);
        return { aktivni: akt, archiv: arch };
    }, [hledane]);

    const allCatIds = useMemo(() => [...collectCatIds(katStrom), BEZ_KAT], [katStrom]);
    const allExpanded = allCatIds.length > 0 && allCatIds.every(id => expanded.has(id));
    const hasTree = allCatIds.length > 1;
    const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(allCatIds));

    // Kategorie → množina ID celého podstromu (filtr „Kategorie" bere i potomky).
    const potomciKategorie = useMemo(() => {
        const m = new Map();
        const sber = (n) => {
            const ids = [n.id];
            for (const c of n.children || []) ids.push(...sber(c));
            m.set(n.id, new Set(ids));
            return ids;
        };
        for (const n of katStrom || []) sber(n);
        return m;
    }, [katStrom]);

    const odpovidaVolbe = useCallback((skupina, it, volba) => {
        switch (skupina) {
            case 'alergeny': return stavAlergenu(it) === volba;
            case 'alergen': return (it.alergeny || []).some(a => a.id === volba);
            case 'cena': return (it.aktualni_cena != null ? 'ma' : 'bez') === volba;
            case 'vyvoj': return smerZmenyCeny(it) === volba;
            case 'pouziti': return ((it.kusovniky_jako_ingredience_count || 0) > 0 ? 'ano' : 'ne') === volba;
            default: return true;
        }
    }, []);

    // `vynech` = skupina, kterou nepočítat — pro počty u voleb té skupiny.
    const odpovidaFiltrum = useCallback((it, f, vynech = null) => {
        if (vynech !== 'kategorie' && f.kategorie != null) {
            if (!potomciKategorie.get(f.kategorie)?.has(it.kategorie_id)) return false;
        }
        if (vynech !== 'dodavatel' && f.dodavatel != null) {
            if (!(it.dodavatele_ids || []).includes(Number(f.dodavatel))) return false;
        }
        for (const skupina of ['alergeny', 'alergen', 'cena', 'vyvoj', 'pouziti']) {
            if (skupina === vynech || !f[skupina].length) continue;
            if (!f[skupina].some(v => odpovidaVolbe(skupina, it, v))) return false;
        }
        return true;
    }, [potomciKategorie, odpovidaVolbe]);

    // Předdefinované pohledy — co se u surovin řeší nejčastěji.
    const pohledy = useMemo(() => ({
        aktivni,
        bezCeny: aktivni.filter(it => it.aktualni_cena == null),
        alergeny: aktivni.filter(it => stavAlergenu(it) === 'nevyplneno'),
        zdrazilo: aktivni.filter(it => smerZmenyCeny(it) === 'zdrazilo'),
        nepouzite: aktivni.filter(it => !(it.kusovniky_jako_ingredience_count > 0)),
        archiv,
    }), [aktivni, archiv]);

    const vRezimuArchiv = pohled === 'archiv';
    const pohledSada = pohledy[pohled] || aktivni;
    const zobrazene = useMemo(() => pohledSada.filter(it => odpovidaFiltrum(it, filtry)), [pohledSada, filtry, odpovidaFiltrum]);

    const skupinyFiltru = useMemo(() => {
        const sPocty = (id, volby) => volby.map(v => ({
            ...v,
            pocet: pohledSada.filter(it => odpovidaFiltrum(it, filtry, id) && odpovidaVolbe(id, it, v.value)).length,
        }));

        // V nabídce jen dodavatelé, od kterých nějaká surovina má cenu.
        const nazvy = new Map(dodavatele.map(d => [d.id, d.nazev]));
        const idsDodavatelu = new Set();
        for (const it of polozky) for (const id of it.dodavatele_ids || []) idsDodavatelu.add(id);
        const dodavatelVolby = [...idsDodavatelu]
            .map(id => ({ value: id, label: nazvy.get(id) || `Dodavatel #${id}` }))
            .sort((a, b) => a.label.localeCompare(b.label, 'cs'));

        const alergenVolby = [...alergenyList]
            .sort((a, b) => (a.cislo_eu ?? 99) - (b.cislo_eu ?? 99))
            .map(a => ({ value: a.id, label: `${a.cislo_eu} ${a.nazev}` }));

        return [
            { id: 'kategorie', label: 'Kategorie', typ: 'dropdown', placeholder: 'Všechny kategorie',
                options: [{ value: '', label: 'Všechny kategorie' }, ...kategorieVolby(katStrom)] },
            { id: 'dodavatel', label: 'Dodavatel', typ: 'dropdown', placeholder: 'Všichni dodavatelé',
                options: [{ value: '', label: 'Všichni dodavatelé' }, ...dodavatelVolby] },
            { id: 'alergeny', label: 'Alergeny', typ: 'volby', volby: sPocty('alergeny', [
                { value: 'obsahuje', label: 'Obsahuje alergeny' }, { value: 'bez', label: 'Bez alergenů' }, { value: 'nevyplneno', label: 'Nevyplněno' },
            ]) },
            ...(alergenVolby.length ? [{ id: 'alergen', label: 'Obsahuje alergen', labelKratky: 'Alergen', typ: 'volby', volby: sPocty('alergen', alergenVolby) }] : []),
            { id: 'cena', label: 'Cena', typ: 'volby', volby: sPocty('cena', [
                { value: 'ma', label: 'Má platnou cenu' }, { value: 'bez', label: 'Bez ceny' },
            ]) },
            { id: 'vyvoj', label: 'Poslední změna ceny', labelKratky: 'Cena', typ: 'volby', volby: sPocty('vyvoj', [
                { value: 'zdrazilo', label: 'Zdražilo' }, { value: 'zlevnilo', label: 'Zlevnilo' }, { value: 'stejna', label: 'Beze změny' },
            ]) },
            { id: 'pouziti', label: 'Použití v recepturách', labelKratky: 'Použití', typ: 'volby', volby: sPocty('pouziti', [
                { value: 'ano', label: 'Používá se' }, { value: 'ne', label: 'Nepoužívá se' },
            ]) },
        ];
    }, [katStrom, dodavatele, alergenyList, polozky, pohledSada, filtry, odpovidaFiltrum, odpovidaVolbe]);

    const pocetFiltru = pocetAktivnichFiltru(skupinyFiltru, filtry);
    const zmenFiltr = (id, hodnota) => setFiltry(f => ({ ...f, [id]: hodnota }));
    const zrusFiltry = () => setFiltry(PRAZDNE_FILTRY);

    // Strom schová prázdné kategorie a vše rozbalí, jakmile se sada zúží.
    const filtrAktivni = searchActive || pohled !== 'aktivni' || pocetFiltru > 0;
    const rozbaleni = hasTree && !filtrAktivni && !vRezimuArchiv ? { vse: allExpanded, prepni: toggleAll } : null;

    // Na mobilu jen „nová surovina". Zakládání kategorií je stavba stromu —
    // správcovská práce, která patří na desktop s plnou editací; tady by jen
    // ubírala místo hledání a nesla riziko překliku.
    const akce = [
        { id: 'novy', label: 'Nová surovina', Icon: Plus, onClick: onNew, primarni: true },
        ...(kompaktni ? [] : [{ id: 'novaKat', label: 'Nová kategorie', Icon: FolderPlus, onClick: () => otevritNovouKat.current?.() }]),
    ];

    const radek = ({ item, active, selected, depthPad, onClick, onDragStartItem }) => (
        <MaterialListRow key={item.id} item={item} active={active} selected={selected}
            depthPad={depthPad} onClick={onClick} onDragStartItem={onDragStartItem}
            siroky={siroky} sloupce={sloupce} sAlergeny
            isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor} />
    );

    // Souhrn kategorie v přehledu — ve stejných sloupcích jako suroviny.
    const kategorieBunky = ({ polozky: vKategorii }) => {
        const alergeny = new Map();
        const dodavatelIds = new Set();
        const zmeny = [];
        let nevyplneno = 0, pouzito = 0, problemu = 0;
        for (const it of vKategorii) {
            for (const a of it.alergeny || []) alergeny.set(a.id, a);
            for (const id of it.dodavatele_ids || []) dodavatelIds.add(id);
            if (stavAlergenu(it) === 'nevyplneno') nevyplneno++;
            pouzito += it.kusovniky_jako_ingredience_count || 0;
            if (it.validace?.stav === 'red') problemu++;
            const z = zmenaCeny(it);
            if (z != null) zmeny.push(z);
        }
        zmeny.sort((a, b) => a - b);
        const stred = zmeny.length >> 1;
        const medianZmeny = !zmeny.length ? null
            : zmeny.length % 2 ? zmeny[stred] : (zmeny[stred - 1] + zmeny[stred]) / 2;
        return (
            <>
                <AlergenyBunka alergeny={[...alergeny.values()]} nevyplneno={nevyplneno} souhrn mutedColor={mutedColor} isDark={isDark} />
                {sloupce?.dodavatel !== false && <DodavatelBunka pocet={dodavatelIds.size} souhrn mutedColor={mutedColor} />}
                <CenaBunka souhrn medianZmeny={medianZmeny} pocetZmen={zmeny.length} mutedColor={mutedColor} />
                {sloupce?.cenaOd !== false && <div style={{ width: GRID_MAT.cenaOd, flexShrink: 0 }} />}
                <PouzitoBunka pocet={pouzito} souhrn mutedColor={mutedColor} />
                <StavBunka problemu={problemu} mutedColor={mutedColor} />
            </>
        );
    };

    const prazdnyText = {
        bezCeny: 'Všechny aktivní suroviny mají platnou cenu',
        alergeny: 'Všechny aktivní suroviny mají vyplněné alergeny',
        zdrazilo: 'Žádná surovina od posledního ceníku nezdražila',
        nepouzite: 'Všechny aktivní suroviny se v recepturách používají',
    }[pohled];

    return (
        /* Na mobilu není seznam karta, ale rovnou plocha displeje — **žádné
           pozadí**, průhledně na pozadí aplikace. Sklo (`cardStyle`,
           `backdrop-filter: blur(40px)`) je dobré pro kartu plovoucí nad
           stránkou; roztažené přes obrazovku jen rozmaže brandovou záři pod
           sebou a jeho hrana se čte jako předěl. */
        <div className={`overflow-hidden flex flex-col ${kompaktni ? '' : 'rounded-xl'}`}
            style={kompaktni ? { flex: 1, minHeight: 0 } : { ...cardStyle, flex: 1, minHeight: 0 }}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni}
                isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: search, onChange: setSearch, placeholder: 'Hledat surovinu / kód…', placeholderKratky: 'Hledat / kód…' }}
                akce={akce}
                pod={vyber.vybrane.size > 0 && (
                    <div className="flex items-center gap-2 rounded-lg px-2 py-1.5"
                        style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)' }}>
                        <span className="text-[11px] font-semibold" style={{ color: '#3B82F6' }}>
                            {pocetPolozek(vyber.vybrane.size)}
                        </span>
                        <span className="text-[11px] flex-1 truncate" style={{ color: mutedColor }}>přetáhni do kategorie</span>
                        <button onClick={vyber.zrus} title="Zrušit výběr (Esc)"
                            className="flex items-center justify-center rounded-md"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2, flexShrink: 0 }}>
                            <X size={13} />
                        </button>
                    </div>
                )}
                filtr={(
                    <FiltrTlacitko skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        pocetVysledku={zobrazene.length} siroky={siroky} isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                )}
                pohled={(
                    <>
                        <PohledPill Icon={Check} label="Aktivní" pocet={pohledy.aktivni.length}
                            aktivni={pohled === 'aktivni'} onClick={() => setPohled('aktivni')}
                            title="Všechny suroviny v používání" isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                        <PohledPill Icon={CircleDollarSign} label="Bez ceny" pocet={pohledy.bezCeny.length}
                            aktivni={pohled === 'bezCeny'} upozorneni onClick={() => setPohled('bezCeny')}
                            title="Suroviny bez platné ceny — do kalkulace receptur vstupují nulou"
                            isDark={isDark} mutedColor={mutedColor} barva={MARZE_BARVY.kriticka} kompaktni={kompaktni} />
                        <PohledPill Icon={ShieldQuestion} label="Alergeny nevyplněny" pocet={pohledy.alergeny.length}
                            aktivni={pohled === 'alergeny'} upozorneni onClick={() => setPohled('alergeny')}
                            title="Suroviny, u kterých nikdo nevyplnil alergeny ani nepotvrdil, že žádné nemají"
                            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                        <PohledPill Icon={TrendingUp} label="Zdražilo" pocet={pohledy.zdrazilo.length}
                            aktivni={pohled === 'zdrazilo'} onClick={() => setPohled('zdrazilo')}
                            title="Aktuální cena je vyšší než předchozí cena od téhož dodavatele"
                            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                        <PohledPill Icon={PackageX} label="Nepoužité" pocet={pohledy.nepouzite.length}
                            aktivni={pohled === 'nepouzite'} onClick={() => setPohled('nepouzite')}
                            title="Aktivní suroviny, které nejsou v žádné receptuře — kandidáti do archivu"
                            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                        <PohledPill Icon={Archive} label="Archiv" pocet={pohledy.archiv.length}
                            aktivni={vRezimuArchiv} onClick={() => setPohled('archiv')}
                            title="Vyřazené suroviny" isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                    </>
                )}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={skupinyFiltru} hodnoty={filtry} onChange={zmenFiltr} onReset={zrusFiltry}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                )}
            />

            {/* Pruh „N položek" + rozbalit/sbalit vše na mobilu odpadá: počet už
                nese pill pohledu v hlavičce a hromadné rozbalení je gesto pro
                myš na velké tabulce, ne pro palec nad pěti kategoriemi. */}
            {!loading && zobrazene.length > 0 && !kompaktni && (siroky
                ? <HlavickaMrizkyMaterialu isDark={isDark} sloupce={sloupce} rozbaleni={rozbaleni} />
                : <HlavickaUzka isDark={isDark} pocet={zobrazene.length} rozbaleni={rozbaleni} />)}

            <div className="overflow-y-auto flex-1" style={{
                flex: 1, minHeight: 0, paddingTop: siroky ? 6 : 0,
                ...(rezervaDole ? { paddingBottom: rezervaDole, overscrollBehavior: 'contain' } : null),
            }}>
                {loading ? (
                    <ListSkeleton isDark={isDark} />
                ) : zobrazene.length === 0 ? (
                    pocetFiltru > 0 ? (
                        <div className="text-center py-8">
                            <p className="text-xs" style={{ color: mutedColor }}>Žádná surovina neodpovídá filtru</p>
                            <button type="button" onClick={zrusFiltry} className="text-xs font-semibold mt-2"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR }}>Zrušit filtry</button>
                        </div>
                    ) : vRezimuArchiv ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Archiv je prázdný</p>
                    ) : prazdnyText ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>{prazdnyText}</p>
                    ) : searchActive ? (
                        <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Nic nenalezeno pro „{search}"</p>
                    ) : (
                        <div className="text-center px-6 py-10">
                            <Package size={28} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                            <p className="text-xs" style={{ color: mutedColor }}>Zatím žádné suroviny</p>
                            <button onClick={onNew} className="flex items-center gap-1 mt-3 mx-auto rounded-lg"
                                style={{ background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>
                                <Plus size={13} /> Vytvořit první
                            </button>
                        </div>
                    )
                ) : vRezimuArchiv ? (
                    // Archiv naplocho — kategorie u vyřazených položek nic neříká.
                    <div style={{ opacity: 0.8 }}>
                        {zobrazene.map(it => radek({
                            item: it, active: activeId === it.id, selected: false, depthPad: 0,
                            onClick: () => onSelect(it.id),
                        }))}
                    </div>
                ) : (
                    <RecipeCategoryTree
                        katStrom={katStrom} items={zobrazene}
                        activeId={activeId} onSelect={onSelect}
                        searchActive={filtrAktivni} siroky={siroky} sloupce={sloupce}
                        isDark={isDark} mutedColor={mutedColor} secondaryColor={secondaryColor}
                        expanded={expanded} setExpanded={setExpanded}
                        vybrane={vyber.vybrane} onKlikRadek={vyber.klikNaRadek}
                        zobrazitViditelnost={false}
                        renderRow={radek}
                        renderKategorieBunky={siroky ? kategorieBunky : null}
                        onCatSave={onCatSave} onCatAdd={onCatAdd} onCatDelete={onCatDelete}
                        onMoveItems={(ids, catId) => { onMoveItems?.(ids, catId); vyber.zrus(); }}
                        onReparentCat={onReparentCat}
                        skrytPridatKategorii onRegisterNovaKategorie={registrujNovouKat}
                        onKategorieKlik={onKategorieKlik}
                        kompaktni={kompaktni}
                    />
                )}
            </div>
        </div>
    );
}
